import React, { useState, useEffect, useCallback } from "react";
import { getRequest } from "../../utils/httpRequest";

// Internal cost/margin view (admin only): reconcile OwlPay's monthly billing.
// OwlPay bills us a harbor fee (our cost); the commission is our revenue the
// customer pays. Monthly netting: Net = Σ commissions − Σ harbor fees
// (negative = we owe OwlPay).
const BASE = "/admin/liquidity_pool_reconciliation";

// Default to the current month as "YYYY-MM".
const defaultMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// "YYYY-MM" → { from (inclusive), to (exclusive, first day of next month) } UTC.
const monthRange = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const from = `${ym}-01T00:00:00Z`;
  const nextMonth = m === 12 ? 1 : m + 1;
  const nextYear = m === 12 ? y + 1 : y;
  const to = `${nextYear}-${String(nextMonth).padStart(2, "0")}-01T00:00:00Z`;
  return { from, to };
};

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const fmtAmount = (v, currency) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;
};

const OwlPayReconciliationCard = ({ colors, showToast }) => {
  const [month, setMonth] = useState(defaultMonth());
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (ym) => {
      const { from, to } = monthRange(ym);
      const q = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      setLoading(true);
      try {
        const res = await getRequest(`${BASE}/owlpay_fees_summary?${q}`);
        if (res && res.success && res.data) {
          setData(res.data);
        } else {
          throw new Error((res && res.error) || "Failed to load OwlPay reconciliation");
        }
      } catch (err) {
        console.error(err);
        const msg = (err && err.data && err.data.error) || err.message || "Request failed";
        showToast("OwlPay reconciliation: " + msg, "danger");
        setData(null);
      } finally {
        setLoading(false);
      }

      // Per-transfer drill-down, independent of the summary; a failure here
      // just leaves the table empty, it doesn't blank the totals.
      try {
        const txRes = await getRequest(`${BASE}/owlpay_fees_transactions?${q}`);
        const rows = txRes && txRes.success
          ? Array.isArray(txRes.data)
            ? txRes.data
            : txRes.data?.transactions || txRes.data?.rows || []
          : [];
        setTxns(rows);
      } catch (err) {
        console.error(err);
        setTxns([]);
      }
    },
    [showToast]
  );

  useEffect(() => {
    load(month);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const net = data ? Number(data.netSettlement) : 0;
  const netLabel = net < 0 ? "We owe OwlPay" : net > 0 ? "OwlPay owes us" : "Settled";
  const netColor = net < 0 ? colors.danger : net > 0 ? colors.success : colors.gray600;

  const card = {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: "10px",
    padding: "20px",
    marginBottom: "20px",
  };
  const metric = {
    flex: "1 1 160px",
    padding: "14px 16px",
    borderRadius: "8px",
    background: colors.gray100,
  };
  const metricLabel = { fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: colors.gray600, fontWeight: 600, marginBottom: "6px" };
  const metricValue = { fontSize: "22px", fontWeight: 700 };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: colors.primary }}>OwlPay Fee Reconciliation</h3>
          <p style={{ margin: "4px 0 0", fontSize: "12px", color: colors.gray500 }}>
            Compare OwlPay's monthly invoice against the harbor fees we were billed. Internal, admin only.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              if (e.target.value) load(e.target.value);
            }}
            style={{ padding: "8px 10px", border: `1px solid ${colors.border}`, borderRadius: "6px", fontSize: "14px" }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "20px", textAlign: "center", color: colors.gray500 }}>Loading OwlPay reconciliation…</div>
      ) : !data ? (
        <div style={{ padding: "20px", textAlign: "center", color: colors.gray500 }}>No data for this period.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={metric}>
              <div style={metricLabel}>Harbor Fee (our cost)</div>
              <div style={{ ...metricValue, color: colors.accent }}>{money(data.harborFeeTotal)}</div>
            </div>
            <div style={metric}>
              <div style={metricLabel}>Commission (our revenue)</div>
              <div style={{ ...metricValue, color: colors.success }}>{money(data.commissionTotal)}</div>
            </div>
            <div style={{ ...metric, background: net < 0 ? "#fef2f2" : net > 0 ? "#f0fdf4" : colors.gray100 }}>
              <div style={metricLabel}>Net Settlement</div>
              <div style={{ ...metricValue, color: netColor }}>{money(data.netSettlement)}</div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: netColor, marginTop: "2px" }}>{netLabel}</div>
            </div>
            <div style={metric}>
              <div style={metricLabel}>Transactions</div>
              <div style={metricValue}>{data.count ?? 0}</div>
            </div>
          </div>

          {Number(data.estimatedCount) > 0 && (
            <div style={{ marginTop: "12px", fontSize: "12px", color: colors.warningText || "#856404", background: colors.warningBg || "#fff3cd", border: "1px solid #ffeeba", borderRadius: "6px", padding: "8px 12px" }}>
              ⚠ {data.estimatedCount} of {data.count} accrued from the cost model (OwlPay returned no per-transfer fee), not actuals.
            </div>
          )}

          {/* Per-transfer drill-down */}
          {txns.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: colors.primary, marginBottom: "8px" }}>
                Transactions ({txns.length}), the transfers behind the totals above
              </div>
              <div style={{ overflowX: "auto", border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      {["Date", "User", "Direction", "Amount", "Harbor fee", "Commission", "Type", "Ref"].map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", borderBottom: `1px solid ${colors.border}`, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: colors.gray600, whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t, i) => {
                      const rowTd = { padding: "8px 10px", borderBottom: `1px solid ${colors.gray100}`, whiteSpace: "nowrap" };
                      return (
                        <tr key={t.transactionId || i}>
                          <td style={rowTd}>{fmtDate(t.createdAt)}</td>
                          <td style={{ ...rowTd, fontFamily: "monospace", fontSize: "11px", color: colors.gray600 }}>{t.userId || "-"}</td>
                          <td style={rowTd}>{t.direction || "-"}</td>
                          <td style={rowTd}>{fmtAmount(t.sourceAmount, t.sourceCurrency)}</td>
                          <td style={{ ...rowTd, color: colors.accent, fontWeight: 600 }}>{money(t.harborFeeAmount)}</td>
                          <td style={{ ...rowTd, color: colors.success, fontWeight: 600 }}>{money(t.commissionAmount)}</td>
                          <td style={rowTd}>
                            <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 600, background: t.estimated ? (colors.warningBg || "#fff3cd") : "#e6f4ea", color: t.estimated ? (colors.warningText || "#856404") : "#137333" }}>
                              {t.estimated ? "Accrued (cost-model)" : "Actual"}
                            </span>
                          </td>
                          <td style={{ ...rowTd, fontFamily: "monospace", fontSize: "11px", color: colors.gray600 }}>{t.externalRef || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OwlPayReconciliationCard;
